import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bot, Send } from 'lucide-react';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { sendDiscoveryMessage, type CelebrationDiscoveryResult } from '@/lib/api/chatDiscovery.service';
import { writeCrossModeDraft } from '@/lib/celebrations/crossModeDraft';

interface DiscoveryMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Chat-first entry for authenticated creation. Only identifies template + tags;
 * the quick/wizard form (or the full chat) takes over from the choice card.
 */
export const CelebrationDiscoveryChat: React.FC = () => {
  const { t } = useTranslation(['celebrations']);
  const navigate = useNavigate();
  const [messages, setMessages] = useState<DiscoveryMessage[]>([
    { role: 'assistant', content: t('celebrations:discovery.greeting') },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CelebrationDiscoveryResult | null>(null);
  const [choiceDismissed, setChoiceDismissed] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    const history = messages.slice(1);
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setError(null);
    setLoading(true);
    setChoiceDismissed(false);

    try {
      const next = await sendDiscoveryMessage({
        message: text,
        history,
        known: result
          ? {
              celebration_type: result.celebration_type,
              celebration_subtype: result.celebration_subtype,
              template_key: result.template_key,
              tags: result.tags,
              volunteered: result.volunteered,
            }
          : undefined,
      });
      setResult(next);
      setMessages((prev) => [...prev, { role: 'assistant', content: next.reply }]);
    } catch {
      setError(t('celebrations:discovery.error'));
    } finally {
      setLoading(false);
    }
  };

  const writeDraft = () => {
    if (!result?.template_key) return;
    writeCrossModeDraft({
      celebration_type: result.celebration_type,
      celebration_subtype: result.celebration_subtype,
      template: result.template_key,
      tags: result.tags,
      celebrant_names: result.volunteered.celebrant_names,
      event_date: result.volunteered.event_date,
      location_name: result.volunteered.location_name,
    });
  };

  const continueInForm = () => {
    writeDraft();
    navigate('/quick');
  };

  const continueInFullChat = () => {
    writeDraft();
    navigate('/celebrations/new?mode=chat');
  };

  const showChoice = Boolean(result?.ready && result.template_key && !choiceDismissed);
  const templateLabel = result?.candidates.find((c) => c.key === result.template_key)?.label
    ?? result?.template_key?.replace(/_/g, ' ')
    ?? '';

  return (
    <PageContainer width="default" className="space-y-4">
      <AppBreadcrumbs />
      <section className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">{t('celebrations:discovery.title')}</h1>
        <p className="text-muted-foreground">{t('celebrations:discovery.subtitle')}</p>
      </section>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="max-h-[50vh] space-y-3 overflow-y-auto" aria-live="polite">
            {messages.map((message, index) => (
              <div key={index} className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start gap-2'}>
                {message.role === 'assistant' && <Bot className="mt-1 h-5 w-5 shrink-0 text-primary" />}
                <p
                  className={
                    message.role === 'user'
                      ? 'max-w-[80%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground'
                      : 'max-w-[80%] rounded-lg bg-muted px-3 py-2 text-sm'
                  }
                >
                  {message.content}
                </p>
              </div>
            ))}
            {loading && <p className="text-sm text-muted-foreground">{t('celebrations:discovery.thinking')}</p>}
            <div ref={endRef} />
          </div>

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

          <form onSubmit={handleSend} className="flex gap-2">
            <Input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={t('celebrations:discovery.placeholder')}
              maxLength={1000}
              aria-label={t('celebrations:discovery.placeholder')}
            />
            <Button type="submit" disabled={loading || input.trim().length === 0} className="gap-1">
              <Send className="h-4 w-4" />
              {t('celebrations:discovery.send')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {showChoice && (
        <Card className="border-primary/40 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">
              {t('celebrations:discovery.foundTitle', { label: templateLabel })}
            </CardTitle>
            <CardDescription>{t('celebrations:discovery.foundSubtitle')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button onClick={continueInForm}>{t('celebrations:discovery.continueForm')}</Button>
            <Button variant="outline" onClick={continueInFullChat}>
              {t('celebrations:discovery.keepChatting')}
            </Button>
          </CardContent>
        </Card>
      )}

      <Button variant="link" className="px-0" onClick={() => navigate('/celebrations/new?mode=choose')}>
        {t('celebrations:discovery.useForm')}
      </Button>
    </PageContainer>
  );
};
